import React, { useState } from 'react';

const SUCCESS_FEEDBACK_MS = 2000;

interface KeywordsFloatingBarProps {
  count: number;
  locale: string;
  visible: boolean;
  copyLabel: string;
  downloadLabel: string;
  copiedLabel: string;
  downloadedLabel: string;
  onCopy: () => void | Promise<void>;
  onDownload: () => void;
}

function CheckIcon() {
  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
      />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
      />
    </svg>
  );
}

const KeywordsFloatingBar: React.FC<KeywordsFloatingBarProps> = ({
  count,
  locale,
  visible,
  copyLabel,
  downloadLabel,
  copiedLabel,
  downloadedLabel,
  onCopy,
  onDownload,
}) => {
  const [copied, setCopied] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  if (!visible || count <= 0) {
    return null;
  }

  const formattedCount = count.toLocaleString(locale);

  const successButtonClass = 'rounded-full p-2 text-primary bg-primary/15 transition-all duration-200';
  const defaultButtonClass =
    'rounded-full p-2 text-primary transition-all duration-200 hover:bg-primary/10 active:bg-primary/15';

  const handleCopy = async () => {
    try {
      await onCopy();
      setCopied(true);
      window.setTimeout(() => setCopied(false), SUCCESS_FEEDBACK_MS);
    } catch {
      // Parent handles errors
    }
  };

  const handleDownload = () => {
    try {
      onDownload();
      setDownloaded(true);
      window.setTimeout(() => setDownloaded(false), SUCCESS_FEEDBACK_MS);
    } catch {
      // Parent handles errors
    }
  };

  return (
    <div
      className="fixed bottom-5 left-5 z-[200] pointer-events-auto"
      dir="ltr"
      aria-label={copyLabel}
    >
      <div className="relative">
        <div className="flex items-center gap-0.5 rounded-full border border-primary/10 bg-white px-3 py-2.5 shadow-[0_8px_24px_rgba(0,0,0,0.12)]">
          <button
            type="button"
            onClick={handleDownload}
            className={downloaded ? successButtonClass : defaultButtonClass}
            aria-label={downloadLabel}
            title={downloaded ? downloadedLabel : downloadLabel}
          >
            {downloaded ? <CheckIcon /> : <DownloadIcon />}
          </button>

          <span className="mx-0.5 h-5 w-px bg-primary/15" aria-hidden="true" />

          <button
            type="button"
            onClick={handleCopy}
            className={copied ? successButtonClass : defaultButtonClass}
            aria-label={copyLabel}
            title={copied ? copiedLabel : copyLabel}
          >
            {copied ? <CheckIcon /> : <CopyIcon />}
          </button>
        </div>

        <div
          className="absolute -bottom-2.5 -right-2 min-w-[2.75rem] rounded-full border-2 border-white bg-primary px-2 py-0.5 text-center text-sm font-bold leading-tight text-white shadow-md"
          aria-live="polite"
        >
          {formattedCount}
        </div>
      </div>
    </div>
  );
};

export default KeywordsFloatingBar;
